/**
 * Contextual help for step pages and the workshop on-ramp.
 * Use **double asterisks** in step strings to bold in-app control names.
 */

export interface HelpMenuItem {
  label: string;
  description: string;
}

export interface HelpSectionContent {
  title: string;
  description: string;
  details?: string;
  steps?: string[];
  menuItems?: HelpMenuItem[];
}

export const APP_MENU_HELP: HelpSectionContent = {
  title: 'The menu (☰)',
  description: 'Tap the menu icon in the top-right corner of any page:',
  menuItems: [
    {
      label: 'Profile',
      description:
        'Update your display name and phone, manage program preferences, and add or edit your sponsor (including email for sharing step summaries).',
    },
    {
      label: 'Invite',
      description: 'Invite a sponsor, sponsee, or friend to Recovery Commons.',
    },
    {
      label: 'Feedback',
      description: 'Send suggestions, report a problem, or ask a question about the app.',
    },
    {
      label: 'Help',
      description:
        'How step work, sharing, and the menu work in Recovery Commons — the same guide as the ? button on step pages.',
    },
    {
      label: 'Save & start over',
      description:
        'Archive your current step work and begin a fresh pass through the steps. Your prior work is kept in the archive.',
    },
    {
      label: 'Backup & Restore',
      description: 'Export or restore an encrypted backup file on your device.',
    },
    {
      label: 'About',
      description: 'Information about Recovery Commons.',
    },
    {
      label: 'Privacy',
      description: 'How your data is protected and how sharing works.',
    },
    {
      label: 'Sign out',
      description: 'Sign out of your account.',
    },
  ],
};

export const APP_STEP_WORK_HELP_STEPS = [
  'Each step has a list of actions. Check **Completed** when you finish an action. Completed actions collapse to keep the page manageable. Some actions include **Read Green Book**, **Read Guidance**, or **Watch Video** buttons, or fields where you enter your personal step work — questions, inventories, prayers, plans, and similar.',
  'Use the **?** help button at the top of any step page to reopen this guide.',
  'When an action offers **Share with sponsor**, use it to preview or email a summary of your progress so you and your sponsor can review your work together. Add your sponsor\'s email in **Profile** (see the menu section below) to make sharing easier. Sharing is optional — you can check off the action once you have discussed your work, even if you do not send anything.',
  'Use the **?** buttons inside forms when you need help with a specific field. You can uncheck an action if you marked it complete too soon. Share links expire after 7 days for privacy.',
];

export const APP_STEP_WORK_HELP: HelpSectionContent = {
  title: 'Working your steps in Recovery Commons',
  description:
    'Recovery Commons is your private workspace for tracking 12-step work — inventories, prayers, plans, and progress. Your data saves automatically when you are signed in.',
  steps: [
    'From **SAA**, open **Work my steps** (the 12 Steps tab). Start with **Getting Started** and go from there.',
    ...APP_STEP_WORK_HELP_STEPS,
  ],
};

export const STEPS_TAB_HELP: HelpSectionContent = {
  title: 'The Work my steps tab',
  description:
    'This tab is your checklist for working through SAA\'s 12 steps. Each card is a step — tap one to open it and work through its actions.',
  steps: [
    'Start with **Getting Started**, then continue with **Step 1** through **Step 12** at your own pace.',
    'When you are signed in, each card shows a status badge: **Not Started** (you have not begun), **In Progress** (you have completed at least one action), or **Completed** (all actions in that step are done).',
    'Statuses update automatically as you work. Open any step to pick up where you left off.',
  ],
};

export const APP_STEP_WORK_ON_TAB_HELP: HelpSectionContent = {
  title: 'Working your steps in Recovery Commons',
  description:
    'Recovery Commons is your private workspace for tracking 12-step work — inventories, prayers, plans, and progress. Your data saves automatically when you are signed in.',
  steps: APP_STEP_WORK_HELP_STEPS,
};

export const WORKSHOP_PAGE_HELP: HelpSectionContent = {
  title: 'The Workshop page',
  description:
    'The Workshop tab is your home for joining this workshop\'s Zoom meeting each week, connecting with the group, and opening your step work. **Workshop** in the page header (top of the app) brings you back here from anywhere in Recovery Commons.',
  steps: [
    '**Join live · Zoom** shows the weekly workshop meeting time. Use **Open Zoom meeting** or **Copy link** to join.',
    '**Work my steps** opens your private SAA step checklist. The **?** button beside it opens this help.',
    '**First Step presentation sign-up** — when open, sign up for a slot to share your First Step in a workshop session.',
    '**Workshop Participants** — roster and contact options for members who confirmed attendance.',
    '**Share & invite** — link and text to invite others to the workshop.',
    '**Workshop calendar** — where we are in the workshop and what\'s next.',
    '**My preferences for this workshop** — phone list, email list, and availability to sponsor (visible only to workshop members).',
    '**Leave the workshop** — remove yourself from the roster or interest list. Your Recovery Commons step work is not affected.',
  ],
};

export const WORKSHOP_STEP_WORK_HELP: HelpSectionContent = {
  title: 'Working your steps in Recovery Commons',
  description:
    'Use Recovery Commons to track step work privately during the workshop. This is separate from the workshop Zoom meeting — it is your personal workspace.',
  steps: [
    'From the workshop page, click **Work my steps** to open **SAA** → **Work my steps** (the 12 Steps tab). Start with **Getting Started** and go from there.',
    'Each step has a list of actions. Check **Completed** when you finish an action. Progress saves automatically. Some actions include readings, guidance, or fields where you enter your personal step work — questions, inventories, prayers, plans, and similar.',
    'Use the **?** help button at the top of any step page to reopen this guide.',
    'When an action offers **Share with sponsor**, use it to preview or email a summary of your progress so you and your sponsor can review your work together. Add your sponsor\'s email in **Profile** (see the menu section below) to make sharing easier. Sharing is optional — you can check off the action once you have discussed your work, even if you do not send anything.',
  ],
};

export type StepHelpKey = 'app' | 'workshop' | 'steps-tab';

export function getStepHelpPanelTitle(stepKey: StepHelpKey): string {
  if (stepKey === 'workshop') return 'Help: Workshop';
  return 'Help: Recovery Commons';
}
